import { useOutletContext, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/utils/constants/routesConstants';
import { convertUnixTimestamp } from '@/helpers/convertUnixTimestamp';

import InfoPanel from '@/components/common/InfoPanel/InfoPanel';
import InfoItem from '@/components/common/InfoItem/InfoItem';

// Sections
import CustomerInformation from '@/containers/Store/ProfileView/sections/CustomerInformation';
import DeliveryData from '@/containers/Store/ProfileView/sections/DeliveryData';
import StoreInformation from '@/containers/Store/ProfileView/sections/StoreInformation';
import SegmentInformation from '@/containers/Store/ProfileView/sections/SegmentInformation';

const SettingsView = () => {
  const { customerData } = useOutletContext();
  const navigate = useNavigate();

  const latestTerm =
    customerData?.termsAccepted && customerData?.termsAccepted?.length > 0
      ? customerData?.termsAccepted[0]
      : null;


  /**
   * Handles navigation to the customer view page when a customer ID is clicked
   * This function takes a customer ID and navigates to the customer view route
   * by replacing the :customerId parameter in the route with the actual ID
   *
   * @param {string} customerId - The ID of the customer to view
   */
  const handleCustomerClick = customerId => {
    navigate(ROUTES.CUSTOMER_VIEW.replace(':customerId', customerId));
  };

  return (
    <div className='bg-gray-850 rounded-lg py-4'>
      <InfoPanel
        title='Authentication & Security'
        isCollapsible={false}
        defaultOpen={true}>
        <div className='space-y-6 px-4 text-sm'>
          <div className='grid grid-cols-3 gap-y-6'>
            <InfoItem
              label='OTP ID (Email ID, Phone No.)'
              value={customerData?.oneTimePasswordData?.otpId}
            />
            <InfoItem
              label='Recovery OTP'
              value={customerData?.oneTimePasswordData?.recoveryOtp}
            />
            <InfoItem
              label='Device Type'
              value={customerData?.oneTimePasswordData?.deviceType}
            />

            <InfoItem
              label='Device Token'
              value={customerData?.oneTimePasswordData?.deviceToken}
            />
            <InfoItem
              label='Code Create Time'
              value={
                customerData?.oneTimePasswordData?.codeCreateTime
                  ? convertUnixTimestamp(customerData?.oneTimePasswordData?.codeCreateTime)
                  : '-'
              }
            />
            <InfoItem
              label='Code Generated'
              value={customerData?.oneTimePasswordData?.codeGenerated}
            />
          </div>
        </div>
      </InfoPanel>

      <InfoPanel
        title='Verification & Security Status'
        isCollapsible={false}
        defaultOpen={true}>
        <div className='space-y-6 px-4 text-sm'>
          <div className='grid grid-cols-3 gap-y-6'>
            <InfoItem
              label='Mobile Verify Status'
              value={
                customerData?.verificationAndSecurityStatus?.mobileVerifiedStatus ?? '-'
              }
            />
            <InfoItem
              label='Mail Verify Status'
              value={
                customerData?.verificationAndSecurityStatus?.mailVerifiedStatus ?? '-'
              }
            />
            <InfoItem
              label='Verification Attempt'
              value={
                customerData?.verificationAndSecurityStatus?.verificationAttempt ?? 'Not available'
              }
            />
          </div>
        </div>
      </InfoPanel>

      <InfoPanel
        title='Company Information'
        isCollapsible={false}
        defaultOpen={true}>
        <div className='space-y-6 px-4 text-sm'>
          <div className='grid grid-cols-3 gap-y-6'>
            <InfoItem
              label='Is Notified'
              value={
                customerData?.companyInformation?.isNotified ? 'Yes' : 'No'
              }
            />
            <InfoItem
              label='Number Update in Progress'
              value={
                customerData?.companyInformation?.numberUpdateInProgress
                  ? 'Yes'
                  : 'No'
              }
            />
          </div>
        </div>
      </InfoPanel>

      <InfoPanel
        title='Commercial Consent'
        isCollapsible={false}
        defaultOpen={true}>
        <div className='space-y-6 px-4 text-sm'>
          <div className='grid grid-cols-3 gap-y-6'>
            <InfoItem
              label='Push Notification'
              value={
                customerData?.commercialConsent?.pushNotification ? 'Yes' : 'No'
              }
            />
            <InfoItem
              label='Email Notification'
              value={
                customerData?.commercialConsent?.emailNotification ? 'Yes' : 'No'
              }
            />
            <InfoItem
              label='SMS Notification'
              value={
                customerData?.commercialConsent?.smsNotification ? 'Yes' : 'No'
              }
            />

            <InfoItem
              label='Call Notification'
              value={
                customerData?.commercialConsent?.callNotification ? 'Yes' : 'No'
              }
            />
          </div>
        </div>
      </InfoPanel>

      <InfoPanel title='Preferences' isCollapsible={false} defaultOpen={true}>
        <div className='space-y-6 px-4 text-sm'>
          <div className='grid grid-cols-3 gap-y-6'>
            <InfoItem
              label='SMS Permissions'
              value={customerData?.preferences?.smsPermissions ?? '-'}
            />
            <InfoItem
              label='Digital Platform Type'
              value={customerData?.preferences?.digitalPlatformType ?? '-'}
            />
            <InfoItem
              label='Email Permission'
              value={customerData?.preferences?.emailPermission ?? '-'}
            />

            <InfoItem
              label='Whatsapp Permission'
              value={customerData?.preferences?.whatsAppPermission ?? '-'}
            />
            <InfoItem
              label='Execution Type'
              value={customerData?.preferences?.executionType ?? '-'}
            />
            <InfoItem
              label='Push notification Permission'
              value={customerData?.preferences?.pushNotificationPermission ?? '-'}
            />

            <InfoItem
              label='Business Type'
              value={customerData?.preferences?.businessType ?? '-'}
            />
            
            <InfoItem
              label='Obtain Permission'
              value={customerData?.preferences?.obtainPermission ?? '-'}
            />

            <InfoItem
              label='Cookie Permission'
              value={customerData?.preferences?.cookiePermission ? 'Yes' : 'No'}
            />
            <InfoItem
              label='Allow Newsletter Notification'
              value={
                customerData?.preferences?.allowNewsletterNotification
                  ? 'Yes'
                  : 'No'
              }
            />
            <InfoItem
              label='Allow Email Notification'
              value={
                customerData?.preferences?.allowEmailNotification
                  ? 'Yes'
                  : 'No'
              }
            />
          </div>
        </div>
      </InfoPanel>

      <InfoPanel
        title='Terms & Conditions'
        isCollapsible={false}
        defaultOpen={true}>
        <div className='space-y-6 px-4 text-sm'>
          <div className='grid grid-cols-3 gap-y-6'>
            <InfoItem label='Content ID' value={latestTerm?.contentId || '-'} />
            <InfoItem
              label='Version Number'
              value={latestTerm?.versionNumber || '-'}
            />
            <InfoItem
              label='Device Serial'
              value={latestTerm?.deviceSerial || '-'}
            />

            <InfoItem
              label='IP Address'
              value={latestTerm?.ipAddress || '-'}
            />
            <InfoItem label='Date' value={latestTerm?.date || '-'} />
            <InfoItem label='Time' value={latestTerm?.time || '-'} />
          </div>
        </div>
      </InfoPanel>
    </div>
  );
};

export default SettingsView;
